import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Eye, Plus, Building2, MoreHorizontal } from 'lucide-react'
import { useTenantOrganizations } from '../hooks/useTenantOrganizations'
import { CreateOrganizationForTenantDialog } from './CreateOrganizationForTenantDialog'

interface TenantOrganizationsTabProps {
  tenantId: string
  tenantName: string
}

export function TenantOrganizationsTab({ tenantId, tenantName }: TenantOrganizationsTabProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const { data: organizations, isLoading, error } = useTenantOrganizations(tenantId)

  const handleOrganizationCreated = () => {
    // Invalidate tenant organizations query to refresh the list
    queryClient.invalidateQueries({ queryKey: ['tenant-organizations', tenantId] })
    setCreateDialogOpen(false)
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-10 text-destructive">
        Failed to load organizations
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {organizations?.length || 0} organization{organizations?.length !== 1 ? 's' : ''} in this tenant
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Organization
        </Button>
      </div>

      {/* Empty state */}
      {(!organizations || organizations.length === 0) ? (
        <div className="text-center py-10 border rounded-md">
          <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">No organizations yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Create the first organization for this tenant.
          </p>
          <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Organization
          </Button>
        </div>
      ) : (
        /* Organizations table */
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Users</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((org: any) => (
                <TableRow key={org._id}>
                  <TableCell>
                    <div
                      className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => navigate(`/organizations/${org._id}`)}
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-medium">{org.displayName}</div>
                        <div className="text-sm text-muted-foreground">{org.name}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{org.domain || '-'}</TableCell>
                  <TableCell>
                    {org.isDeleted ? (
                      <Badge variant="destructive">Deleted</Badge>
                    ) : org.isActive ? (
                      <Badge variant="default">Active</Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">{org.userCount || 0}</TableCell>
                  <TableCell>
                    {new Date(org.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/organizations/${org._id}`)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Organization Dialog */}
      <CreateOrganizationForTenantDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        tenantId={tenantId}
        tenantName={tenantName}
        onSuccess={handleOrganizationCreated}
      />
    </div>
  )
}
